/**
 * PWA self-update orchestration (lib-level, runs once at app start).
 *
 * PROBLEM (2026-08): staff keep AdaptHub PWA windows open for days on multiple
 * devices (laptop + PC). The service worker was only checked for updates at
 * full page loads, so a long-open installed window kept running stale assets
 * and never saw new deployments until the worker was manually unregistered.
 *
 * HOW UPDATING WORKS (vite-plugin-pwa, registerType: 'autoUpdate'):
 * - The generated `sw.js` contains `skipWaiting` + `clientsClaim`, so a newly
 *   downloaded worker activates IMMEDIATELY and takes control of open pages.
 * - The plugin's register module (autoUpdate mode) listens for `activated`
 *   with `isUpdate` and then either calls `onNeedReload()` (if provided) or
 *   reloads the page itself. It does NOT call `registration.update()`
 *   periodically — that trigger is what this module adds.
 *
 * STRATEGY:
 * - Check for a new worker every 30 minutes, on window focus, and on
 *   visibility change (covers laptop lid open / app switch back).
 * - When a new worker has activated:
 *   - Window HIDDEN  → reload instantly (user can't see it happen).
 *   - Window VISIBLE → toast "new version available", reload after 60s at
 *     the latest. (Formerly 15 minutes — but every second of that window the
 *     old bundle is live while its chunks are already deleted, so navigating
 *     to an unvisited view hits a 404. 60s is enough to finish typing a
 *     short input without stranding users on a dead bundle.)
 * - The Vercel `Cache-Control` header on `/sw.js` (vercel.json) guarantees the
 *   update fetches are not served a stale worker from the HTTP cache.
 */
import { registerSW } from 'virtual:pwa-register';
import i18n from '../i18n';
import { showToast } from '../utils';

const CHECK_INTERVAL_MS = 30 * 60 * 1000; // 30 min
const MAX_PENDING_TOAST_MS = 60 * 1000; // force reload 60s after toast

let updateReady = false; // a new worker has activated and is controlling
let reloadDone = false;
let intervalId: ReturnType<typeof setInterval> | null = null;
let pendingReloadTimeout: ReturnType<typeof setTimeout> | null = null;

async function checkForUpdate() {
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    // `update()` re-fetches /sw.js and byte-compares against the current
    // worker. Identical → resolves silently. New → installs, skipWaiting
    // activates it, and the plugin fires `onNeedReload`.
    await reg?.update();
  } catch {
    // Offline / network flap — the next focus or interval tick retries.
  }
}

/** A new worker is live: reload now if unseen, else announce and defer. */
function onNewWorkerActive() {
  if (updateReady) return;
  updateReady = true;
  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
  if (document.visibilityState === 'hidden') {
    reloadNow();
  } else {
    showToast(i18n.t('common.pwa.newVersionToast'), 'success');
    document.addEventListener('visibilitychange', reloadIfHidden);
    pendingReloadTimeout = setTimeout(reloadNow, MAX_PENDING_TOAST_MS);
  }
}

function reloadIfHidden() {
  if (document.visibilityState === 'hidden') reloadNow();
}

function reloadNow() {
  if (reloadDone) return;
  reloadDone = true;
  // Teardown before reload: the pending 60s setTimeout would otherwise fire
  // after the page has already reloaded (harmless but wasteful — and in a
  // PWA window it can trigger a SECOND reload if the new document re-executes
  // a stale closure), and the visibilitychange listener re-fires reloadNow on
  // the next tab switch. Both are dead weight once the reload is committed.
  if (pendingReloadTimeout !== null) {
    clearTimeout(pendingReloadTimeout);
    pendingReloadTimeout = null;
  }
  document.removeEventListener('visibilitychange', reloadIfHidden);
  window.location.reload();
}

/**
 * Registers the service worker and installs the update-check triggers.
 * Call once from main.tsx before the React tree mounts.
 */
export function setupPwaAutoUpdate() {
  if (!('serviceWorker' in navigator)) return;

  if (import.meta.env.DEV) {
    navigator.serviceWorker.getRegistrations().then(registrations => {
      for (const registration of registrations) {
        registration.unregister();
      }
    });
    return;
  }

  registerSW({
    immediate: false,
    // Fired by the plugin when a NEW worker has activated (isUpdate).
    onNeedReload: onNewWorkerActive,
    // Registration failure (private mode, unsupported) — the app still works
    // as a normal website. Nothing to do.
    onRegisterError: () => {},
  });

  window.addEventListener('focus', () => {
    if (!updateReady) void checkForUpdate();
  });
  document.addEventListener('visibilitychange', () => {
    if (!updateReady && document.visibilityState === 'visible') {
      void checkForUpdate();
    }
  });
  // setInterval keeps firing while hidden (modern browsers clamp to >=1s,
  // far below our 30-minute cadence), so this alone catches daily drift.
  intervalId = setInterval(() => {
    void checkForUpdate();
  }, CHECK_INTERVAL_MS);
}
